/**
 * WebcamCapture Component
 * Modal for capturing photos during check-in
 */

import React, { useState, useRef, useEffect } from 'react';
import webcamService from '../../../../services/webcamService';
import Button from '../../../../components/ui/Button';
import Icon from '../../../../components/AppIcon';

const WebcamCapture = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isCaptured, setIsCaptured] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('checking');

  // Initialize camera on mount
  useEffect(() => {
    initializeCamera();

    return () => {
      // Cleanup: stop camera on unmount
      if (streamRef.current) {
        webcamService.stopCamera(streamRef.current);
      }
    };
  }, []);

  const initializeCamera = async () => {
    setIsLoading(true);
    setError(null);

    // Check permission status first
    const permission = await webcamService.checkCameraPermission();
    setPermissionStatus(permission);

    if (permission === 'denied') {
      setError('Camera access is blocked. Please enable camera access in your browser settings and reload.');
      setIsLoading(false);
      return;
    }

    // Start camera
    const { stream, error: cameraError } = await webcamService.startCamera(videoRef.current);

    if (cameraError) {
      setError(cameraError.message);
      setIsLoading(false);
      return;
    }

    streamRef.current = stream;
    setPermissionStatus('granted');
    setIsLoading(false);
  };

  const handleCapture = async () => {
    if (!videoRef.current || !streamRef.current) return;

    // Start countdown
    setCountdown(3);
    
    await webcamService.countdownCapture(3, (remaining) => {
      setCountdown(remaining);
    });

    setCountdown(null);

    // Capture photo
    const { dataUrl, blob, error: captureError } = await webcamService.capturePhoto(videoRef.current);

    if (captureError) {
      setError(captureError.message);
      return;
    }

    setCapturedImage(dataUrl);
    setCapturedBlob(blob);
    setIsCaptured(true);
  };

  const handleRetake = () => {
    setIsCaptured(false);
    setCapturedImage(null);
    setCapturedBlob(null);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!capturedImage || !capturedBlob) return;

    console.log('Confirming photo:', {
      hasDataUrl: !!capturedImage,
      blobSize: capturedBlob.size,
      blobType: capturedBlob.type,
      blobConstructor: capturedBlob.constructor.name
    });

    onCapture({ dataUrl: capturedImage, blob: capturedBlob });
  };

  const handleClose = () => {
    if (streamRef.current) {
      webcamService.stopCamera(streamRef.current);
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-white px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Icon name="Camera" className="h-5 w-5" />
            Check-In Photo
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <Icon name="X" className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error State */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" className="h-5 w-5 text-red-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                  {permissionStatus === 'denied' && (
                    <button
                      onClick={initializeCamera}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 underline"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Camera View / Preview */}
          <div className="relative aspect-video rounded-xl overflow-hidden">
            {/* Video Element (hidden when captured) */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${isCaptured ? 'hidden' : ''}`}
              style={{ transform: 'scaleX(-1)' }} // Mirror front camera
            />

            {/* Captured Image Preview */}
            {isCaptured && capturedImage && (
              <img
                src={capturedImage}
                alt="Captured"
                className="w-full h-full object-cover"
              />
            )}

            {/* Loading Overlay */}
            {isLoading && !error && (
              <div className="absolute inset-0 bg-gray-900 flex flex-col items-center justify-center">
                <Icon name="Loader2" className="h-10 w-10 text-white animate-spin" />
                <p className="text-white mt-3">Accessing camera...</p>
              </div>
            )}

            {/* Countdown Overlay */}
            {countdown !== null && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-8xl font-bold text-white animate-pulse">
                  {countdown}
                </div>
              </div>
            )}

            {/* Camera Frame Guide */}
            {!isLoading && !isCaptured && !countdown && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Center circle guide */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-4 border-white/30 rounded-full" />
                
                {/* Instructions */}
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-white text-sm bg-black/40 inline-block px-4 py-2 rounded-full">
                    Position your face in the frame
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            {!isCaptured ? (
              <>
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCapture}
                  variant="primary"
                  disabled={isLoading || error}
                  className="flex-1"
                >
                  <Icon name="Camera" className="h-4 w-4 mr-2" />
                  Capture
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleRetake}
                  variant="secondary"
                  className="flex-1"
                >
                  <Icon name="RotateCcw" className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="primary"
                  className="flex-1"
                >
                  <Icon name="Check" className="h-4 w-4 mr-2" />
                  Use Photo
                </Button>
              </>
            )}
          </div>

          {/* Tips */}
          <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Photos are securely stored and only visible to you and your employer</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebcamCapture;
